import { Component, OnInit, ViewChild, ElementRef, Renderer2, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { takeUntil, take, switchMap, map, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';

import {
  RegisterService,
  RegisterRequest,
  Address,
} from '../../core/services/register.service';
import { formatCnpj } from '../../core/utils/cnpj.util';
import {
  ViaCEPService,
  ViaCEPResponse,
} from '../../core/services/viacep.service';
import { ToastService } from '../../shared/services/toast.service';
import { UrlService } from '../../core/services/url.service';
import { ApiService } from '../../core/services/api.service';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import {
  PasswordRequirements,
} from '../../shared/components/password-requirements/password-requirements.component';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { GtmService } from '../../core/services/gtm.service';
import {
  BRAZIL_STATES,
  BrazilState,
} from '../../shared/constants/brazil-states.constant';
import { CustomValidators } from '../../shared/validators/custom-validators';
import { IBGECity } from '../../shared/interfaces/ibge-city.interface';
import { UserFacade } from '../../core/store/user/user.facade';
import { RegisterMovelFlowService } from '../../core/services/register-movel-flow.service';
import { I18nService } from '../../core/services/i18n.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { CnpjLookupService } from '../../core/services/cnpj-lookup.service';

import { RegisterStep1Component } from './steps/step1/register-step1.component';
import { RegisterStep2Component } from './steps/step2/register-step2.component';
import { RegisterStep3Component } from './steps/step3/register-step3.component';

@Component({
  selector: 'umkt-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    FooterComponent,
    AuthLayoutComponent,
    RegisterStep1Component,
    RegisterStep2Component,
    RegisterStep3Component,
    I18nPipe
    // keep PasswordRequirementsComponent available to children via their own imports
  ],
  templateUrl: './register.component.html',
  styleUrls: [
    '../../../assets/style/login-register.component.scss',
    './register.component.scss'
  ],
})
export class RegisterComponent implements OnInit, OnDestroy {
  // ==================== VIEW CHILDREN ====================
  @ViewChild('passwordInput') passwordInput!: CustomInputComponent;
  @ViewChild('confirmPasswordInput') confirmPasswordInput!: CustomInputComponent;

  // ==================== FORM GROUPS ====================
  step1Form!: FormGroup;
  step2Form!: FormGroup;
  step3Form!: FormGroup;

  // ==================== STEP MANAGEMENT ====================
  currentStep = 1;
  loading = false;

  // ==================== CNPJ VALIDATION ====================
  isLoadingCNPJ = false;

  // ==================== ADDRESS & LOCATION ====================
  cities: string[] = [];
  filteredCities: string[] = [];
  selectedState = '';
  isLoadingCities = false;
  citySelectedFromList = false;
  private cityValidationTimeout: any;

  // ==================== UI STATE ====================
  showCitySuggestions = false;
  selectedSuggestionIndex = -1;

  // ==================== PASSWORD VALIDATION ====================
  passwordRequirements: PasswordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    hasNumber: false,
  };

  // ==================== CONSTANTS ====================
  readonly brazilStates: BrazilState[] = BRAZIL_STATES;

  // ==================== PRIVATE PROPERTIES ====================
  private destroy$ = new Subject<void>();
  private documentClickListener: (() => void) | null = null;
  private cnpjBlurListener: (() => void) | null = null;
  private isProgrammaticCityUpdate = false;
  private cityAutoFilled = false;
  private isProgrammaticStateUpdate = false;
  private lastResolvedCity = '';
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private registerService = inject(RegisterService);
  private viaCEPService = inject(ViaCEPService);
  private toastService = inject(ToastService);
  private gtm = inject(GtmService);
  private http = inject(HttpClient);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private urlService = inject(UrlService);
  private apiService = inject(ApiService);
  private userFacade = inject(UserFacade);
  private registerMovelFlow = inject(RegisterMovelFlowService);
  private i18nService = inject(I18nService);
  private channelEventsService = inject(ChannelEventsService);
  private cnpjLookupService = inject(CnpjLookupService);


  // ==================== LIFECYCLE METHODS ====================
  ngOnInit(): void {
    //this.checkTokenAndRedirect();
    this.initializeForms();
    this.setupFormListeners();
    this.setupEventListeners();
    
    // Verificar se veio do login para ir direto ao step2
    if (this.route.snapshot.queryParams['step'] === '2') {
      this.currentStep = 2;
      
      // Se vier do fluxo móvel, preencher endereço automaticamente
    }

    // Pré-preencher CNPJ quando vier do fluxo UTM (ex.: redirecionado do carrinho)
    const cnpjParam = this.route.snapshot.queryParams['cnpj'];
    if (cnpjParam) {
      const clean = this.registerService.cleanDocument(cnpjParam);
      if (clean.length === 14) {
        this.step1Form.patchValue({
          cnpj: formatCnpj(clean)
        });
        this.fetchCNPJInfo(clean);
      }
    }

    // Pré-preencher CEP e número do modal de localização quando vier do fluxo UTM
    const cepParam = this.route.snapshot.queryParams['cep'];
    if (cepParam) {
      const cleanCep = (cepParam || '').replace(/\D/g, '');
      if (cleanCep.length === 8) {
        this.step2Form.patchValue({
          cep: this.viaCEPService.formatarCEP(cleanCep)
        });
      }
    }
    const numeroParam = this.route.snapshot.queryParams['numero'];
    if (numeroParam != null && String(numeroParam).trim() !== '') {
      this.step2Form.patchValue({
        numero: String(numeroParam).trim()
      });
    }
  }

  ngOnDestroy(): void {
    this.cleanupEventListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== INITIALIZATION METHODS ====================
 /* private checkTokenAndRedirect(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['token']) {
          this.router.navigate(['/register'], { queryParams: { token: params['token'] } });
        }
      });
  }*/

  private setupEventListeners(): void {
    // Document click listener for city suggestions
    this.documentClickListener = this.renderer.listen('document', 'click', (event: Event) => {
      const cityInput = this.el.nativeElement.querySelector('#cidade');
      const clickedInsideCity = cityInput?.contains(event.target);

      if (!clickedInsideCity) {
        this.showCitySuggestions = false;
        this.selectedSuggestionIndex = -1;
      }
    });

    // CNPJ blur listener
    const cnpjInput = this.el.nativeElement.querySelector('#cnpj');
    if (cnpjInput) {
      this.cnpjBlurListener = this.renderer.listen(cnpjInput, 'blur', () => {
        const cleanCNPJ = this.registerService.cleanDocument(
          this.step1Form.get('cnpj')?.value || ''
        );
        if (cleanCNPJ.length === 14 && this.step1Form.get('cnpj')?.valid) {
          this.fetchCNPJInfo(cleanCNPJ);
        }
      });
    }
  }

  private cleanupEventListeners(): void {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }

    if (this.cnpjBlurListener) {
      this.cnpjBlurListener();
      this.cnpjBlurListener = null;
    }
  }

  // ==================== FORM INITIALIZATION ====================
  private initializeForms(): void {
    this.initializeStep1Form();
    this.initializeStep2Form();
    this.initializeStep3Form();
  }

  private initializeStep1Form(): void {
    this.step1Form = this.fb.group({
      cnpj: this.fb.control('', {
        validators: [Validators.required, CustomValidators.cnpj],
      }),
      razaoSocial: ['', [Validators.required, Validators.maxLength(255)]],
      nomeFantasia: ['', [Validators.required, Validators.maxLength(255)]],
      telefoneEmpresa: ['', [Validators.required, CustomValidators.phone]],
    });
  }

  private initializeStep2Form(): void {
    const isFromMobile = this.route.snapshot.queryParams['returnTo'] === 'register-movel';
    
    this.step2Form = this.fb.group({
      cep: ['', [Validators.required, CustomValidators.cep]],
      endereco: [
        { value: '', disabled: true },
        [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          Validators.maxLength(255),
        ],
      ],
      numero: ['', [Validators.required, CustomValidators.noWhitespaceOnly]],
      complemento: ['', CustomValidators.noWhitespaceOnly],
      bairro: [
        { value: '', disabled: true },
        [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          Validators.maxLength(100),
        ],
      ],
      cidade: [{ value: '', disabled: true }, [Validators.required]],
      estado: [{ value: '', disabled: true }, Validators.required],
      ...(isFromMobile ? {
        fullName: ['', [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          CustomValidators.fullName,
          Validators.maxLength(255),
        ]]
      } : {})
    });

    const initialState = this.step2Form.get('estado')?.value;
    this.selectedState = initialState || '';
  }


  private initializeStep3Form(): void {
    this.step3Form = this.fb.group(
      {
        nomeCompleto: [
          '',
          [
            Validators.required,
            CustomValidators.noWhitespaceOnly,
            CustomValidators.fullName,
            Validators.maxLength(255),
          ],
        ],
        email: ['', [Validators.required, Validators.email, CustomValidators.emailWithDomainValidator()]],
        password: [
          '',
          [Validators.required, CustomValidators.passwordStrength],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validators: CustomValidators.passwordMatch }
    );
  }


  // ==================== FORM LISTENERS ====================
  private setupFormListeners(): void {
    this.setupCEPListener();
    this.setupCNPJListener();
    this.setupPhoneListener();
    this.setupPasswordListener();
    this.setupStateListener();
    this.setupCityInputListener();
  }

  private setupCEPListener(): void {
    this.step2Form.get('cep')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(cep => {
        // Formatação do CEP
        if (cep) {
          const formatted = this.viaCEPService.formatarCEP(cep);
          if (formatted !== cep) {
            this.step2Form.patchValue({ cep: formatted }, { emitEvent: false });
            cep = formatted;
          }
        }

        if (!cep) {
          this.clearAddressForm();
          return;
        }
        
        if (cep && this.viaCEPService.validarCEP(cep)) {
          this.buscarEndereco(cep);
        }
      });
  }

  // ==================== CNPJ VALIDATION METHODS ====================
  private fetchCNPJInfo(cnpj: string): void {
    this.isLoadingCNPJ = true;
    const cleanCNPJ = this.registerService.cleanDocument(cnpj);

    this.cnpjLookupService.fetchOffice(cleanCNPJ)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => this.handleCNPJDataSuccess(data, cleanCNPJ),
        error: err => this.handleCNPJDataError(err),
      });
  }

  private handleCNPJDataSuccess(data: any, cleanCNPJ: string): void {
    this.fillCompanyData(data);
    if (!this.route.snapshot.queryParams['cep']) {
      this.fillAddressData(data);
    }
    this.updateFormValidators();
    this.checkDocumentExists(cleanCNPJ);
    this.isLoadingCNPJ = false;
  }

  private handleCNPJDataError(err: any): void {
    console.error('Erro ao buscar CNPJ:', err);
    this.isLoadingCNPJ = false;
  }

  private fillCompanyData(data: any): void {
    if (data?.company) {
      const razaoSocial = data.company.name || '';
      const nomeFantasia = data.alias || data.company.name;

      this.step1Form.patchValue(
        { razaoSocial, nomeFantasia },
        { emitEvent: false }
      );

      this.step1Form.get('razaoSocial')?.setErrors(null);
      this.step1Form.get('nomeFantasia')?.setErrors(null);
    }
  }

  private fillAddressData(data: any): void {
    if (data?.address) {
      
      
      // Habilita os campos temporariamente para preencher
      this.step2Form.get('endereco')?.enable();
      this.step2Form.get('bairro')?.enable();
      this.step2Form.get('cidade')?.enable();
      this.step2Form.get('estado')?.enable();

      

      this.isProgrammaticCityUpdate = true;

      this.step2Form.patchValue({
        endereco: data.address.street || '',
        numero: data.address.number || '',
        complemento: data.address.details || '',
        bairro: data.address.district || '',
        cep: this.registerService.formatCEP(data.address.zip || ''),
      }, { emitEvent: false });

      this.isProgrammaticStateUpdate = true;
      this.step2Form.get('estado')?.setValue(data.address.state || '', { emitEvent: false });
      this.selectedState = data.address.state || '';
      setTimeout(() => { this.isProgrammaticStateUpdate = false; }, 0);

      const cidadeValue = data.address.city || '';
      this.step2Form.get('cidade')?.setValue(cidadeValue, { emitEvent: false });
      
      this.step2Form.patchValue({ cidade: cidadeValue }, { emitEvent: false });
      
      const cidadeControl = this.step2Form.get('cidade');
      if (cidadeControl) {
        cidadeControl.updateValueAndValidity({ emitEvent: false });
      }
      

      this.citySelectedFromList = true;
      this.cityAutoFilled = true;
      this.lastResolvedCity = cidadeValue;
      this.clearCityError();
      
      if (data.address.state) {
        this.loadCitiesFromIBGE(data.address.state);
      }
      
      
      setTimeout(() => {
        this.applyAddressLockStateAfterCnpj(data.address);

        setTimeout(() => {
          this.isProgrammaticCityUpdate = false;
        }, 100);
      }, 50);
    }
  }


  private applyAddressLockStateAfterCnpj(addr: {
    street?: string;
    number?: string;
    details?: string;
    district?: string;
    zip?: string;
    state?: string;
    city?: string;
  }): void {
    const street = (addr?.street || '').trim();
    const district = (addr?.district || '').trim();
    const state = (addr?.state || '').trim();
    const city = (addr?.city || '').trim();

    if (street) {
      this.step2Form.get('endereco')?.disable({ emitEvent: false });
    } else {
      this.step2Form.get('endereco')?.enable({ emitEvent: false });
    }

    if (district) {
      this.step2Form.get('bairro')?.disable({ emitEvent: false });
    } else {
      this.step2Form.get('bairro')?.enable({ emitEvent: false });
    }

    if (state) {
      this.isProgrammaticStateUpdate = true;
      this.step2Form.get('estado')?.disable({ emitEvent: false });
      setTimeout(() => {
        this.isProgrammaticStateUpdate = false;
      }, 0);
    } else {
      this.step2Form.get('estado')?.enable({ emitEvent: false });
    }

    if (city) {
      this.citySelectedFromList = true;
      this.cityAutoFilled = true;
      this.lastResolvedCity = city;
    } else {
      this.citySelectedFromList = false;
      this.cityAutoFilled = false;
      this.lastResolvedCity = '';
      this.step2Form.get('cidade')?.enable({ emitEvent: false });
    }
  }

  private updateFormValidators(): void {
    this.step1Form.get('telefoneEmpresa')?.setValidators([Validators.required]);
    this.step1Form.get('telefoneEmpresa')?.updateValueAndValidity();
    this.step1Form.updateValueAndValidity();
  }

  private checkDocumentExists(cleanCNPJ: string): void {
    this.registerService
      .checkDocumentExists('cnpj', cleanCNPJ)
      .pipe(takeUntil(this.destroy$))
      .subscribe(exists => this.handleDocumentExistsResult(exists));
  }

  private handleDocumentExistsResult(exists: boolean): void {
    const control = this.step1Form.get('cnpj');
    const currentErrors = control?.errors || {};

    if (exists) {
      control?.setErrors({ ...currentErrors, documentExists: true });
      control?.markAsTouched();
    } else {
      delete currentErrors['documentExists'];
      control?.setErrors(
        Object.keys(currentErrors).length ? currentErrors : null
      );
    }
  }

  private setupCNPJListener(): void {
    this.step1Form.get('cnpj')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((cnpj: string) => {
        const formatted = formatCnpj(cnpj);

        if (formatted !== cnpj) {
          this.step1Form.patchValue({ cnpj: formatted }, { emitEvent: false });
        }

        const cleanCNPJ = this.registerService.cleanDocument(formatted);

        if (cleanCNPJ.length === 14 && this.step1Form.get('cnpj')?.valid) {
          this.fetchCNPJInfo(cleanCNPJ);
        } else {
          this.step1Form.patchValue(
            {
              razaoSocial: '',
              nomeFantasia: '',
              telefoneEmpresa: '',
            },
            { emitEvent: false }
          );
        }
      });
  }

  private setupPhoneListener(): void {
    const phoneControl = this.step1Form.get('telefoneEmpresa');
    phoneControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(phone => {
        if (phone) {
          const formatted = this.registerService.formatPhone(phone);
          if (formatted !== phone) {
            phoneControl.patchValue(formatted, { emitEvent: false });
          }
          const cleaned = formatted.replace(/\D/g, '');
          if (cleaned.length === 10 || cleaned.length === 11) {
            phoneControl.setErrors(null);
          } else {
            phoneControl.setErrors({ invalidPhone: true });
          }
        } else {
          phoneControl.setErrors({ required: true });
        }
      });
  }

  private setupPasswordListener(): void {
    this.step3Form.get('password')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(password => {
        this.step3Form.get('confirmPassword')?.updateValueAndValidity();
        this.updatePasswordRequirements(password || '');
      });
  }


  private setupStateListener(): void {
    this.step2Form.get('estado')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(stateValue => {
        if (this.isProgrammaticStateUpdate) {
          return;
        }

        this.selectedState = stateValue || '';
        
        const cityControl = this.step2Form.get('cidade');
        
        if (!this.isProgrammaticCityUpdate) {
          cityControl?.setValue('');
          this.showCitySuggestions = false;
          this.filteredCities = [];
        }

        if (stateValue) {
          cityControl?.enable();
          this.loadCitiesFromIBGE(stateValue);
        } else {
          cityControl?.disable();
          this.cities = [];
        }
      });
  }

  // ==================== ADDRESS & LOCATION METHODS ====================
  private buscarEndereco(cep: string): void {
    this.loading = true;
    this.showCitySuggestions = false;

    this.viaCEPService.buscarCEP(cep).subscribe({
      next: (response: ViaCEPResponse) => {
        this.fillAddressForm(response);
        this.loading = false;
        this.toastService.showSuccess(
          this.i18nService.instant('pages.register.messages.addressFound'),
          3000,
          'top-center'
        );
      },
      error: () => {
        this.loading = false;
        this.showErrorToast(
        this.i18nService.instant('pages.register.messages.cepNotFound'), 5000);
        this.clearAddressForm();
        this.enableAddressFields(); 
      },
    });
  }

  private fillAddressForm(response: ViaCEPResponse): void {
    
    
    // Habilita os campos temporariamente para preencher
    this.step2Form.get('endereco')?.enable();
    this.step2Form.get('bairro')?.enable();
    this.step2Form.get('cidade')?.enable();
    this.step2Form.get('estado')?.enable();

    

    // Marca como atualização programática
    this.isProgrammaticCityUpdate = true;

    // Preenche todos os campos exceto cidade
      this.step2Form.patchValue({
        endereco: response.logradouro,
        bairro: response.bairro,
      }, { emitEvent: false });

      // Define estado primeiro, separadamente, para evitar listeners indevidos
      this.isProgrammaticStateUpdate = true;
      this.step2Form.get('estado')?.setValue(response.uf, { emitEvent: false });
      this.selectedState = response.uf || '';
      setTimeout(() => { this.isProgrammaticStateUpdate = false; }, 0);

    // Preenche cidade separadamente com múltiplas tentativas
    const cidadeValue = response.localidade;
    
    
    // Primeira tentativa: setValue
    this.step2Form.get('cidade')?.setValue(cidadeValue, { emitEvent: false });
    
    
    // Segunda tentativa: patchValue específico
    this.step2Form.patchValue({ cidade: cidadeValue }, { emitEvent: false });
    
    
    // Terceira tentativa: forçar updateValueAndValidity
    const cidadeControl = this.step2Form.get('cidade');
    if (cidadeControl) {
      cidadeControl.updateValueAndValidity({ emitEvent: false });
      
    }

    

    // Marca as flags ANTES de carregar cidades
    this.citySelectedFromList = true;
    this.cityAutoFilled = true; // Marca que cidade foi preenchida automaticamente
    this.lastResolvedCity = cidadeValue || '';
    this.clearCityError();
    
    

    if (response.uf) {
      this.loadCitiesFromIBGE(response.uf);
    }
    
    
    
    // Delay para garantir que o valor seja aplicado antes de desabilitar
    setTimeout(() => {
      // Desabilita os campos após preencher (preenchimento automático)
      this.disableAddressFields();
      
      
      
      // Reset da flag após um pequeno delay
      setTimeout(() => {
        this.isProgrammaticCityUpdate = false;
      }, 100);
    }, 50);
  }

  private loadCitiesFromIBGE(stateCode: string): void {
    this.isLoadingCities = true;
    const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;

    this.http.get<IBGECity[]>(apiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.cities = data.map(city => city.nome).sort();
          this.isLoadingCities = false;
        },
        error: error => {
          console.error('Erro ao carregar cidades:', error);
          this.cities = [];
          this.isLoadingCities = false;
          this.showErrorToast(
          this.i18nService.instant('pages.register.messages.errorLoadingCities'));        
        },
      });
  }

  private disableAddressFields(): void {
    
    
    
    this.step2Form.get('endereco')?.disable();
    
    
    
    this.step2Form.get('bairro')?.disable();
    
    
    // Mark programmatic state update to avoid clearing city via listener
    this.isProgrammaticStateUpdate = true;
    
    this.step2Form.get('estado')?.disable({ emitEvent: false });
    // small timeout to ensure no race conditions before turning off the guard
    setTimeout(() => {
      this.isProgrammaticStateUpdate = false;
    }, 0);
    
    
    
  }

  private enableAddressFields(): void {
    this.step2Form.get('endereco')?.enable();
    this.step2Form.get('bairro')?.enable();
    this.step2Form.get('estado')?.enable();
    this.step2Form.get('cidade')?.enable();
  }

  private clearAddressForm(): void {
    this.step2Form.patchValue(
      {
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
      },
      { emitEvent: false }
    );

    this.cities = [];
    this.filteredCities = [];
    this.selectedState = '';
    this.citySelectedFromList = false;
    this.cityAutoFilled = false; 
    this.lastResolvedCity = '';
  }



  private setupCityInputListener(): void {
    this.step2Form.get('cidade')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.isProgrammaticCityUpdate) {
          return;
        }

        if (this.cityAutoFilled) {
          return;
        }

        this.clearCityError();
        this.citySelectedFromList = false;

        const cityValue = (value || '').trim();
        if (!cityValue) {
          this.showCitySuggestions = false;
          this.filteredCities = [];
          return;
        }

        this.filteredCities = this.cities
          .filter(city => city.toLowerCase().includes(cityValue.toLowerCase()))
          .slice(0, 10);

        this.showCitySuggestions = this.filteredCities.length > 0;
        this.selectedSuggestionIndex = -1;

        this.validateCityWithDebounce(cityValue);
      });
  }

  // ==================== CITY VALIDATION METHODS ====================
  private validateCityWithDebounce(cityName: string): void {
    if (this.cityValidationTimeout) {
      clearTimeout(this.cityValidationTimeout);
    }

    this.cityValidationTimeout = setTimeout(() => {
      if (this.selectedState && cityName.trim()) {
        this.validateCityInState(this.selectedState, cityName);
      }
    }, 1000);
  }


  onCityKeydown(event: KeyboardEvent): void {
    if (!this.showCitySuggestions || this.filteredCities.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.filteredCities.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(
          this.selectedSuggestionIndex - 1,
          -1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCity(this.filteredCities[this.selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        this.showCitySuggestions = false;
        this.selectedSuggestionIndex = -1;
        break;
    }
  }

  selectCity(city: string): void {
    this.step2Form.get('cidade')?.setValue(city);
    this.showCitySuggestions = false;
    this.filteredCities = [];
    this.selectedSuggestionIndex = -1;

    this.citySelectedFromList = true;
    this.lastResolvedCity = city;

    this.clearCityError();
  }

  private async validateCityInState(
    stateCode: string,
    cityName: string
  ): Promise<boolean> {
    try {
      // Usa cidades já carregadas se disponíveis, senão busca da API
      let cityNames: string[];
      
      if (this.cities.length > 0 && this.selectedState === stateCode) {
        cityNames = this.cities.map(city => city.toLowerCase());
      } else {
        const apiUrl = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`;
        const cities = await firstValueFrom(this.http.get<IBGECity[]>(apiUrl));
        
        if (!cities) {
          this.showErrorToast(this.i18nService.instant('pages.register.messages.errorValidatingCity'));
          return false;
        }
        
        cityNames = cities.map(city => city.nome.toLowerCase());
      }

      const normalizedInput = cityName.toLowerCase().trim();
      const cityExists = cityNames.includes(normalizedInput);

      if (!cityExists) {
        this.handleInvalidCity(cityName);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erro ao validar cidade:', error);
      this.i18nService.instant('pages.register.messages.errorValidatingCity')
      return false;
    }
  }

  private handleInvalidCity(_cityName: string): void {
    const cityControl = this.step2Form.get('cidade');
    if (cityControl) {
      cityControl.setErrors({ invalidCity: true });
      cityControl.markAsTouched();
    }

    setTimeout(() => {
      const cityField = document.getElementById('cidade') as HTMLInputElement;
      if (cityField) {
        cityField.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        cityField.focus();
      }
    }, 100);
  }

  private clearCityError(): void {
    const cityControl = this.step2Form.get('cidade');
    if (cityControl && cityControl.hasError('invalidCity')) {
      const errors = { ...cityControl.errors };
      delete errors['invalidCity'];
      cityControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }
  }



  // ==================== NAVIGATION & STEPS ====================
  async nextStep(): Promise<void> {
    if (this.currentStep === 1 && this.step1Form.invalid) {
      this.showFormErrors(this.step1Form);
      return;
    }

    if (this.currentStep === 2) {
      if (this.step2Form.invalid) {
        this.showFormErrors(this.step2Form);
        return;
      }

      // Validação específica da cidade
      const cityValue = this.step2Form.get('cidade')?.value;
      const stateValue = this.step2Form.get('estado')?.value;

      if (!this.citySelectedFromList) {
        this.showErrorToast(
          this.i18nService.instant('pages.register.messages.selectCity'), 
          4000
        );
        return;
      }

      if (cityValue && stateValue) {
        const isCityValid = await this.validateCityInState(
          stateValue,
          cityValue
        );
        if (!isCityValid) {
          return; // Não avança se a cidade for inválida
        }
      }
    }

    if (this.currentStep < 4) {
      if (this.currentStep === 2 && this.route.snapshot.queryParams['step'] === '2') {
        const returnTo = this.route.snapshot.queryParams['returnTo'];
        if (returnTo === 'register-movel') {

          this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
            const isFirstAccess = loginData?.isFirstAccess ?? false;
          const finalizeRegisterMovelFlow = () => {
            this.registerMovelFlow.reset();
              this.router.navigate(['/']);
            };

            if (isFirstAccess) {
              this.updateMobileUserAddress().subscribe({
                next: () => {
                  this.userFacade.getUserUuid().pipe(take(1)).subscribe(uuid => {
                    if (uuid) {
                      this.userFacade.loadUserDetails(uuid);
                      
                      let navigated = false;
                      
                      this.userFacade.getUserDetails().pipe(
                        filter(user => user !== null && (user.addresses?.length ?? 0) > 0),
                        take(1)
                      ).subscribe(() => {
                        if (!navigated) {
                          navigated = true;
                          finalizeRegisterMovelFlow();
                        }
                      });
                      
                      setTimeout(() => {
                        if (!navigated) {
                          navigated = true;
                          finalizeRegisterMovelFlow();
                        }
                      }, 2000);
                    } else {
                      finalizeRegisterMovelFlow();
                    }
                  });
                },
                error: (error) => {
                  console.error('Erro ao atualizar endereço:', error);
                  this.toastService.showError(
                    'Erro ao salvar endereço. Tente novamente.',
                    5000,
                    'top-center'
                  );
                }
              });
            } else {
              finalizeRegisterMovelFlow();
            }
          });
          return;
        }
      }
      
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      if (this.currentStep === 2 && this.route.snapshot.queryParams['step'] === '2') {
        const returnTo = this.route.snapshot.queryParams['returnTo'];
        if (returnTo === 'register-movel') {
          this.registerMovelFlow.setStage('cnpj-selection');
          this.router.navigate(['/register-movel']);
        } else {
          this.currentStep--;
        }
      } else {
        this.currentStep--;
      }
    } else {
      // Voltar para login (tanto fluxo móvel quanto normal)
      this.goToLogin();
    }
  }

  goToLogin(): void {
    this.registerMovelFlow.reset();
    this.router.navigate(['/login']);
  }

  // ==================== PASSWORD METHODS ====================
  onPasswordToggle(isVisible: boolean): void {
    this.passwordInput.setPasswordVisibility(isVisible);
    this.confirmPasswordInput.setPasswordVisibility(isVisible);
  }

  updatePasswordRequirements(password: string): void {
    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasSpecialChar: /[@$!%*?&#]/.test(password),
      hasNumber: /\d/.test(password),
    };
  }

  // ==================== REGISTRATION METHODS ====================
  submitRegistration(): void {
    if (this.step3Form.invalid) {
      this.showFormErrors(this.step3Form);
      return;
    }

    this.loading = true;
    const userData: RegisterRequest = this.prepareRegistrationData();

    this.registerService.register(userData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: _response => {
          this.loading = false;
          if (_response.status === 'success') {
            this.gtm.signUp('form');
            
            // Send USER_REGISTERED event
            const fullName = `${userData.firstName} ${userData.lastName}`.trim();
            this.channelEventsService.sendUserRegisteredEvent(
              fullName,
              userData.document,
              userData.email
            ).subscribe({
              next: () => {
                console.log('USER_REGISTERED event sent successfully');
              },
              error: (error) => {
                console.error('Error sending USER_REGISTERED event:', error);
              }
            });
            
            this.registerMovelFlow.reset();

            try {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem('umkt_success_register_context', JSON.stringify({
                  email: userData.email,
                  isTokenFlow: false,
                  createdAt: Date.now(),
                }));
              }
            } catch {
              // Ignore storage write failures before redirecting to success.
            }

            this.router.navigate(['/success-register'], {
              state: { email: userData.email },
            });
          } else {
            this.showErrorToast(_response.msg || this.i18nService.instant('pages.register.messages.registerError'), 5000);
          }
        },
        error: error => {
          this.loading = false;
          this.handleRegistrationError(error);
        },
      });
  }


  private prepareRegistrationData(): RegisterRequest {
    const formData = this.extractFormData();
    const nameParts = this.parseFullName(formData.step3Data.nomeCompleto);
    const address = this.createAddress(formData.step2Data);

    return {
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      email: formData.step3Data.email,
      displayName: formData.step1Data.nomeFantasia,
      login: formData.step3Data.email,
      password: formData.step3Data.password,
      confirmPassword: formData.step3Data.confirmPassword,
      document: this.registerService.cleanDocument(formData.step1Data.cnpj),
      documentType: 'CNPJ',
      phone: this.registerService.cleanPhone(formData.step1Data.telefoneEmpresa),
      business: formData.step1Data.razaoSocial,
      role: '2',
      addresses: [address],
      confirmationUrl: this.urlService.buildUrl('/confirm-email', true, true),
    };
  }

  private extractFormData() {
    return {
      step1Data: this.step1Form.value,
      step2Data: this.step2Form.getRawValue(),
      step3Data: this.step3Form.value,
    };
  }

  private parseFullName(nomeCompleto: string): { firstName: string; lastName: string } {
    const trimmedName = nomeCompleto.trim();
    const nameParts = trimmedName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return { firstName, lastName };
  }

  private createAddress(step2Data: any): Address {
    return {
      address: step2Data.endereco,
      neighborhood: step2Data.bairro,
      city: step2Data.cidade,
      state: step2Data.estado,
      country: 'BR',
      zipCode: step2Data.cep,
      complement: step2Data.complemento || '',
      main: true,
      type: 'BILLING',
      number: step2Data.numero,
    };
  }

  private handleRegistrationError(error: any): void {
    console.error('Erro no cadastro:', error);

    let errorMessage = this.i18nService.instant('pages.register.messages.registerError');

    if (error.error?.msg) {
      const serverMsg = error.error.msg;

      // TRADUÇÃO DE ERROS ESPECÍFICOS
      // Mapeia a mensagem exata que vem do backend para uma chave de tradução
      const translations: Record<string, string> = {
        'E-mail is already in use.': this.i18nService.instant('pages.register.messages.emailInUse'),
        'Invalid password.': this.i18nService.instant('pages.register.messages.invalidPassword'),
      };

      // Se encontrar no mapa usa a tradução, senão usa a msg do servidor
      errorMessage = translations[serverMsg] || serverMsg; 

    } else if (error.status === 400) {
      errorMessage = this.i18nService.instant('pages.register.messages.invalidData');
    } else if (error.status === 409) {
      errorMessage = this.i18nService.instant('pages.register.messages.emailInUse');
    }

    this.showErrorToast(errorMessage, 5000);
  }


  // ==================== UTILITY METHODS ====================
  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault();
    }
  }

  private showErrorToast(message: string, duration: number = 3000): void {
    this.toastService.showError(message, duration, 'top-center');
  }

  private showFormErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });

    this.showErrorToast(
      this.i18nService.instant('pages.register.messages.formError'), 
      5000
    );
  }

  isFromMobileFlow(): boolean {
    return this.route.snapshot.queryParams['returnTo'] === 'register-movel';
  }

  private updateMobileUserAddress() {
    return this.userFacade.getUserUuid().pipe(
      take(1),
      switchMap(userId => {
        if (!userId) {
          throw new Error('User ID não encontrado');
        }

        // Habilitar todos os campos temporariamente para garantir que os valores sejam lidos
        this.step2Form.get('endereco')?.enable();
        this.step2Form.get('bairro')?.enable();
        this.step2Form.get('cidade')?.enable();
        this.step2Form.get('estado')?.enable();

        // Usar getRawValue() para pegar valores mesmo quando campos estão disabled
        let formValue = this.step2Form.getRawValue();
        
        return this.getUserDocument().pipe(
          switchMap(document => {
            // Garantir que cidade está habilitada e atualizar valor
            const cidadeControl = this.step2Form.get('cidade');
            cidadeControl?.enable();
            
            // Se cidade estiver vazia, tentar buscar do sessionStorage
            if (!cidadeControl?.value) {
              const cidadeFromStorage = sessionStorage.getItem('mobileFlow_cidade');
              if (cidadeFromStorage) {
                cidadeControl?.setValue(cidadeFromStorage, { emitEvent: false });
              }
            }
            
            cidadeControl?.updateValueAndValidity({ emitEvent: false });
            
            formValue = this.step2Form.getRawValue();
            
            
            let cidadeFinal = formValue.cidade || cidadeControl?.value || this.lastResolvedCity || '';
            if (!cidadeFinal) {
              cidadeFinal = sessionStorage.getItem('mobileFlow_cidade') || '';
              this.lastResolvedCity = cidadeFinal;
            }
            
            const payload = {
              address: formValue.endereco || '',
              neighborhood: formValue.bairro || '',
              city: cidadeFinal,
              state: formValue.estado || '',
              country: 'Brasil',
              zipCode: formValue.cep?.replace(/\D/g, '') || '',
              complement: formValue.complemento || '',
              main: true,
              type: 'Residencial',
              number: formValue.numero || '',
              document: document || '',
              fullName: formValue.fullName || ''
            };

            const headers = {
              'x-api-key': 'MKT-40377bcec9e58aade359178c',
              'Content-Type': 'application/json'
            };

            const endpoint = `/user/update/sue/account/${userId}`;
            return this.apiService.put(endpoint, payload, { headers });
          })
        );
      })
    );
  }

  private getUserDocument(): Observable<string> {
    return this.userFacade.getUserDetails().pipe(
      take(1),
      map(user => user?.document || '')
    );
  }

}
